import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, appId } from '../firebase';

// Live subscription state for the freemium gates.
//
// The billing doc is written EXCLUSIVELY by the Stripe webhook Cloud Function
// (see functions/index.js); firestore.rules make it read-only for clients, so
// no client path can self-upgrade. A missing doc simply means the free plan.
// Because this is a live listener, gates unlock in the open session seconds
// after checkout completes ("the unlock moment").
export const usePlan = (userId, isDemoMode) => {
    const [billing, setBilling] = useState(null);
    const [planReady, setPlanReady] = useState(false);

    useEffect(() => {
        setBilling(null);
        // Demo mode has no Firebase auth at all — any read would be denied.
        if (!userId || isDemoMode) {
            setPlanReady(true);
            return;
        }
        setPlanReady(false);
        const ref = doc(db, `artifacts/${appId}/users/${userId}/billing/subscription`);
        const unsubscribe = onSnapshot(ref, (snap) => {
            setBilling(snap.exists() ? snap.data() : null);
            setPlanReady(true);
        }, (error) => {
            console.error('Error fetching billing status:', error);
            setBilling(null);
            setPlanReady(true);
        });
        return () => unsubscribe();
    }, [userId, isDemoMode]);

    const isPro = billing?.plan === 'pro';

    return {
        plan: isPro ? 'pro' : 'free',
        isPro,
        planReady,
        billingStatus: billing?.status || null,
        currentPeriodEnd: billing?.currentPeriodEnd?.toDate?.() || null,
        cancelAtPeriodEnd: Boolean(billing?.cancelAtPeriodEnd),
        cancelAt: billing?.cancelAt?.toDate?.() || null,
        priceInterval: billing?.priceInterval || null,
        priceNickname: billing?.priceNickname || null,
        // Which discount is riding the subscription right now — the only
        // trustworthy answer to "is this person a founder". See `discountOf`
        // in functions/index.js for why the interval and the nickname are not.
        discountCode: billing?.discountCode || null,
        discountDuration: billing?.discountDuration || null,
    };
};

export default usePlan;
